import { useState, useMemo, useCallback } from 'react';
import Fuse from 'fuse.js';
import { HelpArticle, HelpSearchResult } from '../types/help';
import { helpArticles } from '../data/help/articles';

const fuseOptions = {
  keys: [
    { name: 'title', weight: 0.4 },
    { name: 'keywords', weight: 0.3 },
    { name: 'content', weight: 0.2 },
    { name: 'category', weight: 0.1 },
  ],
  threshold: 0.3,
  includeScore: true,
  minMatchCharLength: 2,
};

export const useHelpSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const fuse = useMemo(() => new Fuse(helpArticles, fuseOptions), []);

  const searchResults = useMemo((): HelpSearchResult[] => {
    if (!searchQuery.trim()) {
      const filtered = selectedCategory
        ? helpArticles.filter(article => article.category === selectedCategory)
        : helpArticles;
      return filtered.map(article => ({ article }));
    }

    const results = fuse.search(searchQuery);
    const filtered = selectedCategory
      ? results.filter(result => result.item.category === selectedCategory)
      : results;

    return filtered.map(result => ({
      article: result.item,
      score: result.score,
    }));
  }, [searchQuery, selectedCategory, fuse]);

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(
      new Set(helpArticles.map(article => article.category))
    );
    return uniqueCategories.sort();
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSelectedCategory(null);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    searchResults,
    categories,
    clearSearch,
  };
};
